import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { GraduationCap, Lightbulb, BookOpen, Megaphone, HeartHandshake, Send, Heart } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import AuthGate from '../components/AuthGate';
import { PageHeader } from '../components/SectionHeader';
import { useAuth } from '../auth/AuthContext';
import { CATEGORIES } from '../data/demo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inp = 'w-full bg-[#0B0C0E] border border-[#2A2F3D] focus:border-amber-500 outline-none rounded-lg px-4 py-2.5 text-white text-sm';
const lbl = 'block text-xs font-mono uppercase tracking-widest text-slate-500 mb-2';

const TABS = [
  { id: 'curso', label: 'Proponer un curso', icon: GraduationCap, desc: 'Escuelas, empresas o profesionales que imparten formación.' },
  { id: 'mejora', label: 'Sugerir una mejora', icon: Lightbulb, desc: 'Ideas, errores o funciones que echas en falta en la web.' },
  { id: 'contenido', label: 'Aportar manual o contenido', icon: BookOpen, desc: 'Manuales, guías, plantillas o hilos para el foro.' },
  { id: 'patrocinio', label: 'Patrocinio / publicidad', icon: Megaphone, desc: 'Marcas y empresas que quieren apoyar Stage Crew.' },
  { id: 'donacion', label: 'Hacer una donación', icon: Heart, desc: 'Apoya económicamente el mantenimiento y desarrollo de la plataforma.' },
  { id: 'otra', label: 'Otra ayuda', icon: HeartHandshake, desc: 'Cualquier otra forma de colaborar con la comunidad.' },
];

const postJson = async (path, body) => {
  const res = await fetch(`${API}${path}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(body) });
  if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(typeof d.detail === 'string' ? d.detail : 'Error'); }
  return res.json();
};

const SubmitBtn = ({ saving, label = 'Enviar propuesta' }) => (
  <button type="submit" data-testid="colabora-submit" disabled={saving} className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-slate-950 font-semibold px-6 py-3 rounded-lg transition-colors">
    <Send size={15} /> {saving ? 'Enviando…' : label}
  </button>
);

const CursoForm = () => {
  const { user } = useAuth();
  const [f, setF] = useState({ title: '', school: '', category: '', modality: 'Presencial', location: '', date: '', duration: '', schedule: '', price: '', places: '', instructor: '', description: '', program: '', requirements: '', enrollUrl: '', contactEmail: user?.email || '' });
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!f.title || !f.school || !f.category || f.description.trim().length < 30) { toast.error('Título, escuela, categoría y una descripción detallada (mín. 30 caracteres) son obligatorios'); return; }
    setSaving(true);
    try { await postJson('/training/proposals', f); setDone(true); toast.success('Curso propuesto · lo revisaremos antes de publicarlo'); }
    catch (err) { toast.error(err.message || 'No se pudo enviar'); } finally { setSaving(false); }
  };
  if (done) return <Done testId="colabora-curso-done" text="Curso recibido. Comprobaremos que la información y el enlace de inscripción estén vigentes antes de publicarlo en Formación." />;
  return (
    <form onSubmit={submit} className="space-y-5" data-testid="colabora-curso-form">
      <p className="text-sm text-slate-400 leading-relaxed">Cada curso publicado en Stage Crew debe tener <span className="text-slate-200">descripción completa, programa, requisitos, fechas, precio y enlace de inscripción activo</span>. Así la gente sabe exactamente qué va a hacer antes de inscribirse.</p>
      <div><label className={lbl}>Título del curso</label><input data-testid="curso-title" className={inp} value={f.title} onChange={(e) => set('title', e.target.value)} placeholder="Certificación grandMA3 Programmer" /></div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div><label className={lbl}>Escuela / empresa</label><input data-testid="curso-school" className={inp} value={f.school} onChange={(e) => set('school', e.target.value)} /></div>
        <div><label className={lbl}>Categoría</label><select data-testid="curso-category" className={inp} value={f.category} onChange={(e) => set('category', e.target.value)}><option value="">Selecciona…</option>{CATEGORIES.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}</select></div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div><label className={lbl}>Modalidad</label><select data-testid="curso-modality" className={inp} value={f.modality} onChange={(e) => set('modality', e.target.value)}>{['Presencial', 'Online', 'Híbrido'].map((m) => <option key={m}>{m}</option>)}</select></div>
        <div><label className={lbl}>Ubicación</label><input className={inp} value={f.location} onChange={(e) => set('location', e.target.value)} placeholder="Barcelona" /></div>
        <div><label className={lbl}>Fecha de inicio</label><input data-testid="curso-date" type="date" className={inp} value={f.date} onChange={(e) => set('date', e.target.value)} /></div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div><label className={lbl}>Duración</label><input className={inp} value={f.duration} onChange={(e) => set('duration', e.target.value)} placeholder="3 días · 24 h" /></div>
        <div><label className={lbl}>Horario</label><input className={inp} value={f.schedule} onChange={(e) => set('schedule', e.target.value)} placeholder="09:30–17:30" /></div>
        <div><label className={lbl}>Precio</label><input data-testid="curso-price" className={inp} value={f.price} onChange={(e) => set('price', e.target.value)} placeholder="890 €" /></div>
        <div><label className={lbl}>Plazas</label><input className={inp} value={f.places} onChange={(e) => set('places', e.target.value)} placeholder="8" /></div>
      </div>
      <div><label className={lbl}>Formador/a</label><input className={inp} value={f.instructor} onChange={(e) => set('instructor', e.target.value)} /></div>
      <div><label className={lbl}>Descripción detallada</label><textarea data-testid="curso-description" rows={5} className={inp + ' resize-none'} value={f.description} onChange={(e) => set('description', e.target.value)} placeholder="A quién va dirigido, qué se aprende, qué se entrega (certificado, material)…" /></div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div><label className={lbl}>Programa (un punto por línea)</label><textarea rows={4} className={inp + ' resize-none'} value={f.program} onChange={(e) => set('program', e.target.value)} /></div>
        <div><label className={lbl}>Requisitos (un punto por línea)</label><textarea rows={4} className={inp + ' resize-none'} value={f.requirements} onChange={(e) => set('requirements', e.target.value)} /></div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div><label className={lbl}>Enlace de inscripción</label><input data-testid="curso-url" type="url" className={inp} value={f.enrollUrl} onChange={(e) => set('enrollUrl', e.target.value)} placeholder="https://…" /></div>
        <div><label className={lbl}>Email de contacto</label><input type="email" className={inp} value={f.contactEmail} onChange={(e) => set('contactEmail', e.target.value)} /></div>
      </div>
      <SubmitBtn saving={saving} label="Proponer curso" />
    </form>
  );
};

const MessageForm = ({ tab }) => {
  const { user } = useAuth();
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const placeholders = {
    mejora: 'Ej.: Me gustaría filtrar las ofertas por fecha de inicio…',
    contenido: 'Ej.: Tengo el manual de la Hog 4 v5.2 en castellano y una plantilla de rider…',
    patrocinio: 'Ej.: Somos distribuidor de X y queremos patrocinar la sección de manuales…',
    otra: 'Cuéntanos cómo te gustaría ayudar.',
  };
  const submit = async (e) => {
    e.preventDefault();
    if (msg.trim().length < 5) { toast.error('Escribe un poco más de detalle'); return; }
    setSaving(true);
    try {
      if (tab === 'mejora') await postJson('/suggestions', { category: 'mejora', message: msg, page: '/colabora' });
      else await postJson('/contact', { name: user?.nick || user?.name || 'Miembro', email: user?.email || '', topic: tab === 'patrocinio' ? 'partnership' : 'colabora', message: `[${tab}] ${msg}` });
      setDone(true); toast.success('¡Gracias! Propuesta enviada al administrador');
    } catch { toast.error('No se pudo enviar'); } finally { setSaving(false); }
  };
  if (done) return <Done testId={`colabora-${tab}-done`} text="Propuesta enviada a Lucas Tornero (administración). Te responderemos por correo." />;
  return (
    <form onSubmit={submit} className="space-y-5" data-testid={`colabora-${tab}-form`}>
      <textarea data-testid="colabora-message" rows={6} className={inp + ' resize-none'} value={msg} onChange={(e) => setMsg(e.target.value)} placeholder={placeholders[tab]} />
      <SubmitBtn saving={saving} />
    </form>
  );
};

const DonacionInfo = () => (
  <div className="space-y-5" data-testid="colabora-donacion-info">
    <p className="text-sm text-slate-400 leading-relaxed">Stage Crew es <span className="text-slate-200">gratuito</span> y queremos que siga siéndolo. Mantener la plataforma tiene costes (servidores, infraestructura, herramientas, desarrollo, mantenimiento e IA). Las donaciones son <span className="text-slate-200">completamente voluntarias</span> y cualquier cantidad ayuda.</p>
    <p className="text-sm text-slate-400 leading-relaxed">Todo el dinero recibido se destina exclusivamente a mantener, mejorar y desarrollar Stage Crew. Puedes donar con tarjeta o con PayPal desde la página de donaciones.</p>
    <div className="flex flex-wrap gap-2">
      {[5, 10, 20, 50].map((v) => (
        <Link key={v} to="/donaciones" data-testid={`colabora-donacion-${v}`} className="rounded-lg border border-[#2A2F3D] hover:border-amber-500/60 text-white font-semibold px-4 py-2.5 text-sm transition-colors">{v} €</Link>
      ))}
    </div>
    <Link to="/donaciones" data-testid="colabora-donacion-cta" className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold px-6 py-3 rounded-lg transition-colors">
      <Heart size={15} /> Ir a la página de donaciones
    </Link>
  </div>
);

const Done = ({ text, testId }) => (
  <div className="sc-card p-6 border-emerald-500/40 bg-emerald-500/5 text-sm text-emerald-300" data-testid={testId}>{text}</div>
);

const Inner = () => {
  const [params, setParams] = useSearchParams();
  const tab = TABS.some((t) => t.id === params.get('tab')) ? params.get('tab') : 'curso';
  const current = TABS.find((t) => t.id === tab);
  return (
    <div className="min-h-screen bg-[#0B0C0E]">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <PageHeader title="Colabora con Stage Crew" subtitle="Esta web la construimos entre todos. Propón cursos, mejoras, manuales o cualquier ayuda: todo llega al administrador para revisarlo y añadirlo." testId="colabora-header" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-16">
          <nav className="space-y-2" data-testid="colabora-tabs">
            {TABS.map((t) => { const Icon = t.icon; const active = t.id === tab; return (
              <button key={t.id} type="button" data-testid={`colabora-tab-${t.id}`} onClick={() => setParams({ tab: t.id })} className={`w-full text-left sc-card p-4 flex items-start gap-3 transition-colors ${active ? 'border-amber-500/60 bg-amber-500/5' : ''}`}>
                <Icon size={18} className={active ? 'text-amber-500 mt-0.5' : 'text-slate-500 mt-0.5'} />
                <div><div className={`text-sm font-semibold ${active ? 'text-amber-500' : 'text-white'}`}>{t.label}</div><div className="text-xs text-slate-500 mt-0.5 leading-snug">{t.desc}</div></div>
              </button>
            ); })}
            <p className="text-[11px] text-slate-500 leading-relaxed px-1 pt-2">¿Prefieres escribir directamente? <Link to="/contacto?topic=colabora" className="text-amber-500 hover:underline">Contacto con administración</Link>.</p>
          </nav>
          <div className="lg:col-span-2 sc-card p-6 md:p-8">
            <h2 className="text-xl font-bold text-white mb-5" style={{ fontFamily: 'Outfit, sans-serif' }}>{current.label}</h2>
            {tab === 'curso' ? <CursoForm /> : tab === 'donacion' ? <DonacionInfo /> : <MessageForm key={tab} tab={tab} />}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

const ColaboraPage = () => <AuthGate eyebrow="Colabora"><Inner /></AuthGate>;
export default ColaboraPage;
